import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  font-family: monospace;
`;

const HeaderValue = styled.span`
  color: ${theme.colors.text};
  font-weight: 600;
`;

const Bar = styled.div`
  display: flex;
  gap: 1px;
  height: 20px;

  /* 32 slots across a phone leaves each ~11px wide, so the touch target has to come from
     height. Not 44px, but enough to hit reliably. */
  ${media.sm} {
    height: 28px;
  }
  /* Rounded corners come from :first-child / :last-child on the slots below rather than
     overflow:hidden, which would clip the selected slot's outline. */
  & > :first-of-type { border-top-left-radius: 3px; border-bottom-left-radius: 3px; }
  & > :last-of-type { border-top-right-radius: 3px; border-bottom-right-radius: 3px; }
`;

// Visual states map sentinel statuses onto a smaller palette to keep the
// timeline scannable.
//   mined    — checkpoint landed on L1 (the only definitive success)
//   valid    — locally re-executed as valid but never landed on L1. Counted
//              as missed at the protocol level (the chain skipped this slot
//              on L1), but rendered distinctly so the user can see that the
//              proposal was attested on p2p — same diagnostic as the pre-v5
//              `checkpoint-proposed` state.
//   pending  — proposal seen, validation in flight (checkpoint-unvalidated).
//              No verdict yet, so excluded from missed/filled accounting.
//   missed   — terminal failure: -invalid / -missed / blocks-missed
//   unknown / current / future as before
//   orphaned — mined, then discarded: the checkpoint reached L1 and the rollup later pruned it
//              away. Drawn as neither success nor failure, because it is neither.
type SlotState =
  | "mined"
  | "valid"
  | "pending"
  | "missed"
  | "orphaned"
  | "unknown"
  | "current"
  | "future";

const Slot = styled.div<{ state: SlotState; pct?: number; selected?: boolean }>`
  flex: 1;
  position: relative;
  cursor: pointer;
  outline: ${(p) => (p.selected ? `2px solid ${theme.colors.primary}` : "none")};
  outline-offset: -1px;
  z-index: ${(p) => (p.selected ? 2 : "auto")};
  background: ${(p) => {
    switch (p.state) {
      case "mined": return theme.colors.success;
      // 'valid' = locally re-executed as valid but no L1 confirmation. Same
      // bucket as the pre-v5 `checkpoint-proposed`: a missed slot, rendered
      // in warning color to set apart from outright proposer failures.
      case "valid": return `${theme.colors.warning}aa`;
      // 'pending' = checkpoint-unvalidated. In-flight; we don't know yet.
      case "pending": return `${theme.colors.warning}66`;
      case "missed": return `${theme.colors.danger}66`;
      // Hatched rather than flat, and in a colour outside the success/warning/danger ramp. A flat
      // fill of any of those three makes a claim about blame that isn't true; the hatch reads as
      // "this was struck out", which is exactly what happened to it. Same visual language as the
      // "not observed" column in EpochMissStrip, which is the nearest existing idea.
      case "orphaned": return `repeating-linear-gradient(-45deg, ${theme.colors.orphaned}cc 0 2px, ${theme.colors.orphaned}44 2px 5px)`;
      case "unknown": return `${theme.colors.textMuted}33`;
      case "current": return theme.colors.bgHover;
      case "future": return theme.colors.bg;
    }
  }};
  transition: background 0.3s;

  ${(p) => p.state === "current" && p.pct != null && `
    &::after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: ${Math.min(p.pct, 100)}%;
      background: ${theme.colors.primary}88;
      transition: width 1s linear;
    }
  `}
`;

const BlockBadge = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 8px;
  font-weight: 700;
  color: ${theme.colors.bg};
  z-index: 1;
`;

/**
 * Slot detail as a line under the bar rather than a floating tooltip.
 *
 * The bar was hover-only, which on a phone meant the per-slot data — status, block range,
 * containing checkpoint — was simply unreachable. Rendering below the bar works for touch and
 * mouse alike, so it replaces the tooltip instead of sitting alongside it.
 */
const Detail = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.xs};
  min-height: 16px;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  color: ${theme.colors.textMuted};
`;

const DetailHint = styled.span`
  font-family: inherit;
  font-style: italic;
`;

function slotDetailLines(s: {
  slot: number;
  state: SlotState;
  blocks: number;
  status: string | null;
  details: { firstBlock: number; lastBlock: number; checkpointNumber: number | null } | null;
}): string[] {
  const lines = [`slot #${s.slot}`];
  if (s.state === "current") lines.push("in progress");
  else if (s.state === "future") lines.push("upcoming");
  else if (s.state === "unknown") lines.push("unknown (pre-indexing)");
  // Named before the status is read, because the status still says `checkpoint-mined` and on its
  // own that is the misleading half of the truth.
  else if (s.state === "orphaned") lines.push("discarded by reorg (mined, then pruned)");
  else if (s.status === "checkpoint-mined") lines.push("mined (L1 confirmed)");
  else if (s.status === "checkpoint-valid") lines.push("missed (validated locally, no L1)");
  else if (s.status === "checkpoint-unvalidated") lines.push("pending (proposal seen, validation in flight)");
  else if (s.status === "checkpoint-invalid") lines.push("missed (proposal rejected on re-execution)");
  else if (s.status === "checkpoint-missed") lines.push("missed (no attestations)");
  else if (s.status === "blocks-missed") lines.push("missed (no activity)");
  else if (s.blocks === 0) lines.push(s.state);

  if (s.details) {
    const { firstBlock, lastBlock, checkpointNumber } = s.details;
    const range = firstBlock === lastBlock ? `block #${firstBlock}` : `blocks #${firstBlock}–#${lastBlock}`;
    lines.push(range);
    if (checkpointNumber != null) lines.push(`checkpoint #${checkpointNumber}`);
  }
  return lines;
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function EpochTimeline({
  epochTimeline,
  genesisTime,
  slotDuration,
  isLive = true,
}: {
  epochTimeline: {
    epochNumber: number;
    epochStartSlot: number;
    slotsPerEpoch: number;
    blocksPerSlot: Record<string, number>;
    slotStatuses: Record<
      string,
      | "checkpoint-mined"
      | "checkpoint-valid"
      | "checkpoint-invalid"
      | "checkpoint-unvalidated"
      | "checkpoint-missed"
      | "blocks-missed"
      | "unknown"
    >;
    slotDetails: Record<string, { firstBlock: number; lastBlock: number; checkpointNumber: number | null }>;
    /**
     * Slots whose work a recorded prune discarded. Optional so a socket payload from a server
     * process that predates the field renders exactly as it does today rather than throwing.
     */
    orphanedSlots?: number[];
  } | null;
  genesisTime: number | null;
  slotDuration: number;
  isLive?: boolean;
}) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [hoveredSlot, setHoveredSlot] = useState<number | null>(null);
  // A tap sticks; hover only previews. The selected slot wins so moving the mouse away on
  // desktop doesn't wipe out what you just clicked.
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(interval);
  }, [isLive]);

  if (!epochTimeline || genesisTime == null) return null;

  const { epochNumber, epochStartSlot, slotsPerEpoch, blocksPerSlot, slotStatuses, slotDetails } = epochTimeline;
  const orphaned = new Set(epochTimeline.orphanedSlots ?? []);
  const currentSlot = isLive ? Math.floor((now - genesisTime) / slotDuration) : -1;
  const slotInEpoch = currentSlot - epochStartSlot;

  // Time remaining in current slot (live only)
  const currentSlotStart = genesisTime + currentSlot * slotDuration;
  const elapsed = now - currentSlotStart;
  const slotPct = (elapsed / slotDuration) * 100;
  const remaining = slotDuration - elapsed;

  const slots: {
    slot: number;
    state: SlotState;
    blocks: number;
    status: string | null;
    details: { firstBlock: number; lastBlock: number; checkpointNumber: number | null } | null;
  }[] = [];
  for (let i = 0; i < slotsPerEpoch; i++) {
    const absSlot = epochStartSlot + i;
    const blocks = blocksPerSlot[String(absSlot)] ?? 0;
    const status = slotStatuses[String(absSlot)] ?? null;
    const details = slotDetails[String(absSlot)] ?? null;
    let state: SlotState;
    if (isLive && absSlot === currentSlot) {
      state = "current";
    } else if (isLive && absSlot > currentSlot) {
      state = "future";
    } else if (orphaned.has(absSlot)) {
      // Ahead of the status switch deliberately. The sentinel still reports these as
      // `checkpoint-mined` — its history lives in the node's kvStore, which an L2 prune does not
      // rewind — so reading the status first is precisely how 32 discarded slots came to be drawn
      // as a full green epoch.
      state = "orphaned";
    } else {
      switch (status) {
        case "checkpoint-mined": state = "mined"; break;
        case "checkpoint-valid": state = "valid"; break;
        case "checkpoint-unvalidated": state = "pending"; break;
        case "checkpoint-invalid":
        case "checkpoint-missed":
        case "blocks-missed": state = "missed"; break;
        default: state = "unknown"; break;
      }
    }
    slots.push({ slot: absSlot, state, blocks, status, details });
  }

  // Header counters mirror the health-endpoint classification:
  //   filled  = mined only (the only state with a definitive L1 confirmation)
  //   missed  = valid + hard missed. 'valid' is the v5 equivalent of the
  //             pre-v5 'checkpoint-proposed' state — locally attested but
  //             never landed on L1; surfaced via the validated sub-counter.
  //   pending = unvalidated (in-flight, no verdict yet — surfaced as a hint)
  //   unknown = pre-indexing or no sentinel data
  const filledCount = slots.filter((s) => s.state === "mined").length;
  const orphanedCount = slots.filter((s) => s.state === "orphaned").length;
  const validCount = slots.filter((s) => s.state === "valid").length;
  const hardMissedCount = slots.filter((s) => s.state === "missed").length;
  const missedCount = validCount + hardMissedCount;
  const pendingCount = slots.filter((s) => s.state === "pending").length;
  const unknownCount = slots.filter((s) => s.state === "unknown").length;
  const futureCount = slots.filter((s) => s.state === "future" || s.state === "current").length;

  const activeSlotNumber = selectedSlot ?? hoveredSlot;
  const activeSlot = slots.find((s) => s.slot === activeSlotNumber) ?? null;

  return (
    <Wrapper>
      <Header>
        {isLive ? (
          <span>
            Epoch <HeaderValue>#{epochNumber}</HeaderValue>
            {" · "}Slot <HeaderValue>{slotInEpoch + 1}/{slotsPerEpoch}</HeaderValue>
            {" · "}<HeaderValue>{remaining > 0 ? formatDuration(remaining) : "transitioning"}</HeaderValue>
          </span>
        ) : (
          <span>
            Epoch <HeaderValue>#{epochNumber}</HeaderValue>
            {" · "}<span style={{ color: theme.colors.textMuted }}>completed</span>
          </span>
        )}
        <span>
          <span style={{ color: theme.colors.success }}>{filledCount}</span>
          {" / "}
          <span style={{ color: theme.colors.danger }}>{missedCount}</span>
          {validCount > 0 && (
            <span style={{ color: theme.colors.warning }}> ({validCount} validated)</span>
          )}
          {orphanedCount > 0 && (
            <>{" / "}<span style={{ color: theme.colors.orphaned }}>{orphanedCount} discarded</span></>
          )}
          {pendingCount > 0 && <>{" / "}<span style={{ color: theme.colors.warning }}>{pendingCount}…</span></>}
          {unknownCount > 0 && <>{" / "}<span style={{ color: theme.colors.textMuted }}>{unknownCount}?</span></>}
          {isLive && <>{" / "}{futureCount} remaining</>}
        </span>
      </Header>
      <Bar>
        {slots.map((s) => (
          <Slot
            key={s.slot}
            state={s.state}
            pct={s.state === "current" ? slotPct : undefined}
            selected={selectedSlot === s.slot}
            onMouseEnter={() => setHoveredSlot(s.slot)}
            onMouseLeave={() => setHoveredSlot(null)}
            onClick={() => setSelectedSlot((prev) => (prev === s.slot ? null : s.slot))}
            role="button"
            aria-label={`Slot ${s.slot}`}
            title={`slot #${s.slot}`}
          >
            {s.blocks > 1 && <BlockBadge>{s.blocks}</BlockBadge>}
          </Slot>
        ))}
      </Bar>
      <Detail>
        {activeSlot ? (
          slotDetailLines(activeSlot).map((line, idx) => <span key={idx}>{line}</span>)
        ) : (
          <DetailHint>Tap a slot for detail</DetailHint>
        )}
      </Detail>
    </Wrapper>
  );
}
